import React, { Fragment, useCallback, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQuery } from "react-query"
import { useSelector } from "react-redux"

import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { BaseSimpleDialog } from "../../components/dialogs/BaseSimpleDialog"
import { SidAPI } from "../../services/SidAPI"
import { queryClient } from "../../store/QueryClientStore"
import type { RootState } from "../../store/ReduxStore"
import type { OrganizationInvitation } from "../../types/generated/authentication/OrganizationInvitation"
import type { Role } from "../../types/generated/authentication/Role"
import { useIsPermitted } from "../../utils/PermissionsHelpers"
import { useLogOut } from "../Auth/helpers"
import type { ContainerProps, MenuOption } from "./Interfaces"
import { MemberCard } from "./MemberCard"
import { MemberInviteDialog } from "./MemberInviteDialog"

/**
 * @param {OrganizationUser} member
 * @param {boolean} isLastMember
 * @return {ReactElement}
 */
export function Container(props: Readonly<ContainerProps>): React.ReactElement {
  const { member, isLastMember = false } = props

  const organizationName = useSelector((state: RootState) => state.auth.organizationName) as string
  const organizationUUID = useSelector((state: RootState) => state.auth.organizationId) as string
  const currentUser = useSelector((state: RootState) => state.auth.email) as string
  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const currentUserRole = useSelector((state: RootState) => state.auth.roleName)

  const flattenedSidPermissions = useSelector((state: RootState) => state.permissions.flattenedSidPermissions)

  const { t } = useTranslation("settings", {
    keyPrefix: "accessControl.sections.members.container",
  })

  // states for controlling opening/closing dialogs
  const [openInviteDialog, setOpenInviteDialog] = useState<boolean>(false)
  const [isMemberRemovalDialogOpen, setIsMemberRemovalDialogOpen] = useState<boolean>(false)
  const [isOrganizationDeletionDialogOpen, setIsOrganizationDeletionDialogOpen] = useState<boolean>(false)

  const LogOut = useLogOut()

  const leaveOrganizationMutation = useMutation<AxiosResponse, AxiosError>(SidAPI.leaveOrganization, {
    onSuccess: () => LogOut({ redirect: true }),
    onError: () =>
      NotificationUtils.toast(t("notifications.logoutError"), {
        severity: "error",
      }),
  })

  const deleteOrganizationMutation = useMutation<AxiosResponse, AxiosError, string>(SidAPI.deleteOrganization, {
    onSuccess: () => LogOut({ redirect: true }),
    onError: () =>
      NotificationUtils.toast(t("notifications.logoutError"), {
        severity: "error",
      }),
  })

  const removeUserMutation = useMutation<AxiosResponse, AxiosError, string>(SidAPI.removeUser, {
    onSuccess: async () => {
      NotificationUtils.toast(
        t("notifications.removeMemberSuccess", { firstName: member.first_name, lastName: member.last_name }),
        {
          severity: "success",
        },
      )

      await queryClient.invalidateQueries(["Organization", organizationUUID])
    },
    onError: () =>
      NotificationUtils.toast(t("notifications.logoutError"), {
        severity: "error",
      }),
  })

  const handleRemove = useCallback((): void => {
    isLastMember ? setIsOrganizationDeletionDialogOpen(true) : setIsMemberRemovalDialogOpen(true)
  }, [isLastMember])

  const canListRoles = useIsPermitted("List roles", permissions.synapse_id, flattenedSidPermissions)
  const canListGroups = useIsPermitted("List groups", permissions.synapse_id, flattenedSidPermissions)
  const canKickUsers = useIsPermitted("Kick users", permissions.synapse_id, flattenedSidPermissions)

  const menuOptions = useMemo((): MenuOption[] => {
    const options: MenuOption[] = []

    canListRoles &&
      canListGroups &&
      member.role_label !== "OWNER" &&
      options.push({
        title: t("menu.edit"),
        variant: "base",
        onClick: () => setOpenInviteDialog(true),
      })

    canKickUsers &&
      options.push({
        title: t("menu.remove"),
        variant: "negative",
        onClick: handleRemove,
      })

    return member.role_label === "OWNER" && currentUserRole !== "OWNER" ? [] : options
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canListRoles, canListGroups, canKickUsers, handleRemove, member.role_label, currentUserRole])

  return (
    <Fragment>
      {/* Member removal/Leaving dialog */}
      <BaseSimpleDialog
        open={isMemberRemovalDialogOpen}
        name={currentUser === member.email ? organizationName : `${member.first_name} ${member.last_name}`}
        mode={currentUser === member.email ? "member-leaving" : "member-removal"}
        onAccept={async () => {
          if (currentUser === member.email) {
            await leaveOrganizationMutation.mutateAsync()
          } else {
            await removeUserMutation.mutateAsync(member.uid)
          }
        }}
        isLoading={currentUser === member.email ? leaveOrganizationMutation.isLoading : removeUserMutation.isLoading}
        onClose={() => {
          setIsMemberRemovalDialogOpen(false)
        }}
      />

      {/* Organization Deletion dialog */}
      <BaseSimpleDialog
        open={isOrganizationDeletionDialogOpen}
        name={organizationName}
        mode={"organization-deletion"}
        onAccept={async () => await deleteOrganizationMutation.mutateAsync(organizationUUID)}
        isLoading={deleteOrganizationMutation.isLoading}
        onClose={() => {
          setIsOrganizationDeletionDialogOpen(false)
        }}
      />

      {openInviteDialog && (
        <MemberInviteDialog isOpen={openInviteDialog} onClose={() => setOpenInviteDialog(false)} member={member} />
      )}

      <MemberCard
        name={member.first_name + " " + member.last_name}
        email={member.email}
        role={member.role_label}
        date={member.date_joined}
        MenuOptions={menuOptions}
      />
    </Fragment>
  )
}

interface PendingMemberContainerProps {
  member: OrganizationInvitation
}
export function PendingMemberContainer(props: Readonly<PendingMemberContainerProps>): React.ReactElement {
  const { member } = props

  const { t } = useTranslation("settings", {
    keyPrefix: "accessControl.sections.members.container",
  })

  const organizationUUID = useSelector((state: RootState) => state.auth.organizationId) as string
  const permissions = useSelector((state: RootState) => state.auth.permissions)

  const flattenedSidPermissions = useSelector((state: RootState) => state.permissions.flattenedSidPermissions)

  const canDeleteUserInvitations = useIsPermitted(
    "Delete user invitations",
    permissions.synapse_id,
    flattenedSidPermissions,
  )

  const [isInvitationDeletionDialogOpen, setIsInvitationDeletionDialogOpen] = useState<boolean>(false)

  // Fetch single role
  const { data: role } = useQuery<AxiosResponse<Role>, AxiosError>(
    ["role", member.role],
    () => SidAPI.fetchRole(member.role.uid),
    {
      enabled: !!organizationUUID && !!member.role,
      refetchOnMount: false,
    },
  )

  const invitationDeletionMutation = useMutation<AxiosResponse, AxiosError, string>(SidAPI.removeInvitation, {
    onSuccess: async () => {
      NotificationUtils.toast(t("notifications.invitationDeletionSuccess", { email: member.email }), {
        severity: "success",
      })

      await queryClient.invalidateQueries(["OrganizationInvitation", organizationUUID])
    },
    onError: () =>
      NotificationUtils.toast(t("notifications.logoutError"), {
        severity: "error",
      }),
  })

  return (
    <Fragment>
      {/* Invitation Deletion dialog */}
      <BaseSimpleDialog
        open={isInvitationDeletionDialogOpen}
        name={member.email ?? ""}
        mode={"invitation-deletion"}
        onAccept={async () => {
          await invitationDeletionMutation.mutateAsync(member.token)
          setIsInvitationDeletionDialogOpen(false)
        }}
        isLoading={invitationDeletionMutation.isLoading}
        onClose={() => {
          setIsInvitationDeletionDialogOpen(false)
        }}
      />

      <MemberCard
        name={member.email}
        role={role?.data.name ?? "USER"}
        date={member.created_at}
        pending
        MenuOptions={
          canDeleteUserInvitations
            ? [
                {
                  title: t("menu.remove"),
                  variant: "negative",
                  onClick: () => {
                    setIsInvitationDeletionDialogOpen(true)
                  },
                },
              ]
            : undefined
        }
      />
    </Fragment>
  )
}
